import {
  apiV2GasMonitorConfigDetailPost,
  apiV2GasMonitorConfigUpsertPost,
  apiV2AreaGetConfigDetailByAreaIdPost,
  V2GasMonitorConfigDetailPostResponse,
  V2GasMonitorConfigUpsertPostRequest,
} from '@maxtropy/device-customer-apis-v2';
import { RegionConfig, MediumItem } from './type';

export async function fetchUseGasConfigDetail(
  form: any,
  setCurrentUnitData: Function,
  setRegionConfigs?: (v: RegionConfig[]) => void,
  setAreaMediumCache?: (v: Record<number, MediumItem[]>) => void
): Promise<V2GasMonitorConfigDetailPostResponse> {
  const res = await apiV2GasMonitorConfigDetailPost({});

  // 回显表单值
  form.setFieldsValue({
    energyTopology: res.topologyId,
    pipeLossNode: res.nodeIds,
    deviceLabel: res.labelIds,
    // 用气能流配置
    ...(res.energyUnitGroupReqs ?? []).reduce((acc: Record<string, any>, item, idx) => {
      const i = idx + 1;
      acc[`energyAnalysisGroup${i}`] = item.energyGroupId;
      acc[`levelRange${i}`] = {
        fromLevel: item.fromLevel,
        toLevel: item.toLevel,
      };
      return acc;
    }, {}),
  });

  // 回显区域配置并加载各区域的能源介质
  if (setRegionConfigs && res.areaMediumLinkRequests) {
    const configs: RegionConfig[] = res.areaMediumLinkRequests.map((item, index) => ({
      key: `region-${item.areaId}-${index}`,
      areaName: item.areaName,
      areaId: item.areaId,
      mediums: (item.mediums ?? []).filter(m => m.mediumId != null) as RegionConfig['mediums'],
    }));
    setRegionConfigs(configs);

    // 加载每个已配置区域的能源介质选项
    if (setAreaMediumCache) {
      const mediumCache: Record<number, MediumItem[]> = {};
      for (const item of res.areaMediumLinkRequests ?? []) {
        if (item.areaId) {
          const areaRes = await apiV2AreaGetConfigDetailByAreaIdPost({ areaId: item.areaId });
          mediumCache[item.areaId] = (areaRes.timePeriodList || [])
            .filter((tp): tp is { mediumId: number; mediumName: string } => {
              return tp.mediumId !== undefined && tp.mediumName !== undefined;
            })
            .map(tp => ({
              mediumId: tp.mediumId,
              mediumName: tp.mediumName,
            }));
        }
      }
      setAreaMediumCache(mediumCache);
    }
  }

  return res;
}

/**
 * 保存/修改用气监控配置
 */
export const onFinish = async (
  values: any,
  regionConfigs: RegionConfig[],
  unitGroup: { name: string; id: number }[],
  setIsEdit: (v: boolean) => void,
  setRefresh: (fn: (prev: boolean) => boolean) => void,
  message: any
) => {
  const resolveGroupName = (energyGroupId: number): string => {
    return unitGroup.find(u => u.id === energyGroupId)?.name ?? '';
  };

  const payload: V2GasMonitorConfigUpsertPostRequest = {
    // 区域及能源介质关系（只发送已选区域）
    areaMediumLinkRequests: regionConfigs.map(item => ({
      areaId: item.areaId!,
      areaName: item.areaName!,
      mediums: item.mediums.map(m => ({
        mediumId: m.mediumId,
        mediumName: m.mediumName,
      })),
    })),
    // 用气能流配置
    energyUnitGroupReqs: [
      {
        energyGroupId: values.energyAnalysisGroup1,
        energyGroupName: resolveGroupName(values.energyAnalysisGroup1),
        fromLevel: values.levelRange1?.fromLevel,
        toLevel: values.levelRange1?.toLevel,
      },
      {
        energyGroupId: values.energyAnalysisGroup2,
        energyGroupName: resolveGroupName(values.energyAnalysisGroup2),
        fromLevel: values.levelRange2?.fromLevel,
        toLevel: values.levelRange2?.toLevel,
      },
      {
        energyGroupId: values.energyAnalysisGroup3,
        energyGroupName: resolveGroupName(values.energyAnalysisGroup3),
        fromLevel: values.levelRange3?.fromLevel,
        toLevel: values.levelRange3?.toLevel,
      },
    ].filter(item => item.energyGroupId),
    topologyId: values.energyTopology,
    nodeIds: values.pipeLossNode,
    labelIds: values.deviceLabel,
  };

  await apiV2GasMonitorConfigUpsertPost(payload);
  message.success('保存成功');
  setIsEdit(false);
  setRefresh(prev => !prev);
};
